import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Select, Spin, Space, message } from 'antd';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { analyticsApi, batchApi } from '../../services/api';
import type { Batch } from '../../types';

const { Option } = Select;

const Analytics: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(7);
  const [selectedBatch, setSelectedBatch] = useState<string>('');
  const [selectedCountry, setSelectedCountry] = useState<string>('');
  const [batches, setBatches] = useState<Batch[]>([]);

  // Chart data
  const [messagesByDay, setMessagesByDay] = useState<any[]>([]);
  const [messagesByHour, setMessagesByHour] = useState<any[]>([]);
  const [messagesByBatch, setMessagesByBatch] = useState<any[]>([]);
  const [messagesByCountry, setMessagesByCountry] = useState<any[]>([]);

  useEffect(() => {
    loadBatches();
    loadAnalytics();
  }, []);

  useEffect(() => {
    loadAnalytics();
  }, [days, selectedBatch, selectedCountry]);

  const loadBatches = async () => {
    try {
      const data = await batchApi.list();
      setBatches(data);
    } catch (error) {
      message.error('Failed to load batches');
    }
  };

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const params = {
        days,
        batch_id: selectedBatch || undefined,
        country: selectedCountry || undefined,
      };

      // Load all analytics data in parallel
      const [dayData, hourData, batchData, countryData] = await Promise.all([
        analyticsApi.getMessagesByDay(params),
        analyticsApi.getMessagesByHour(params),
        analyticsApi.getMessagesByBatch({ days, country: selectedCountry || undefined }),
        analyticsApi.getMessagesByCountry({ days, batch_id: selectedBatch || undefined }),
      ]);

      setMessagesByDay(dayData.data);
      setMessagesByHour(hourData.data);
      setMessagesByBatch(batchData.data);
      setMessagesByCountry(countryData.data);
    } catch (error) {
      message.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const handleDaysChange = (value: number) => {
    setDays(value);
  };

  const handleBatchChange = (value: string) => {
    setSelectedBatch(value);
  };

  const handleCountryChange = (value: string) => {
    setSelectedCountry(value);
  };

  // Get unique countries from the data
  const countries = Array.from(new Set((messagesByCountry || []).map((item) => item.country))).sort();

  return (
    <div>
      <h1>Analytics</h1>

      {/* Filters */}
      <Card style={{ marginBottom: 16 }}>
        <Space size="large" wrap>
          <div>
            <label style={{ marginRight: 8, fontWeight: 500 }}>Time Period:</label>
            <Select value={days} onChange={handleDaysChange} style={{ width: 150 }}>
              <Option value={7}>Last 7 days</Option>
              <Option value={30}>Last 30 days</Option>
              <Option value={90}>Last 90 days</Option>
            </Select>
          </div>

          <div>
            <label style={{ marginRight: 8, fontWeight: 500 }}>Batch:</label>
            <Select
              value={selectedBatch}
              onChange={handleBatchChange}
              style={{ width: 200 }}
              allowClear
              placeholder="All Batches"
            >
              {(batches || []).map((batch) => (
                <Option key={batch.id} value={batch.id}>
                  {batch.name}
                </Option>
              ))}
            </Select>
          </div>

          <div>
            <label style={{ marginRight: 8, fontWeight: 500 }}>Country:</label>
            <Select
              value={selectedCountry}
              onChange={handleCountryChange}
              style={{ width: 150 }}
              allowClear
              placeholder="All Countries"
            >
              {(countries || []).map((country) => (
                <Option key={country} value={country}>
                  {country}
                </Option>
              ))}
            </Select>
          </div>
        </Space>
      </Card>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
        </div>
      ) : (
        <>
          {/* Messages by Day */}
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <Card title="Messages Per Day" bordered={false}>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={messagesByDay}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="count" stroke="#1890ff" strokeWidth={2} name="Messages" />
                  </LineChart>
                </ResponsiveContainer>
              </Card>
            </Col>

            {/* Messages by Hour */}
            <Col xs={24} lg={12}>
              <Card title="Messages Per Hour (24h)" bordered={false}>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={messagesByHour}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="hour" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="count" fill="#52c41a" name="Messages" />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            </Col>
          </Row>

          {/* Messages by Batch and Country */}
          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24} lg={12}>
              <Card title="Messages Per Batch" bordered={false}>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={messagesByBatch}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="batch_name" angle={-45} textAnchor="end" height={100} />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="count" fill="#fa8c16" name="Messages" />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            </Col>

            {/* Messages by Country */}
            <Col xs={24} lg={12}>
              <Card title="Messages Per Country" bordered={false}>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={messagesByCountry}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="country" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="count" fill="#722ed1" name="Messages" />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
};

export default Analytics;
